import { useLocation, useNavigate } from "react-router-dom";
import { Home } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

/**
 * Floating "Back to main website" button that appears on EVERY non-homepage
 * route — public, admin, staff, kiosk, lobby, signage — so visitors and
 * staff always have a one-tap way to return to the public homepage.
 *
 * Style adapts to context:
 *  • Public inner pages: large champagne pill (high visibility for visitors)
 *  • Admin / staff / kiosk / lobby / signage: SMALL discreet pill in the
 *    corner so it doesn't crowd the workspace (Marc's request 2026-06-16).
 */
export default function BackToMainButton() {
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();

  // Hide on the homepage itself — nothing to go back to.
  if (location.pathname === "/") return null;

  // Sep 22 2026 · Karine ask — the Crown Reserve plan pages have their
  // own prominent "Book This Plan" CTA at the bottom of each card.
  // Floating pills were covering the button on mobile so we hide them
  // on this route (the Navbar still has a home link).
  if (location.pathname.startsWith("/crown-reserve-plan")) return null;

  // Feb 2026 · Karine ask — hide the floating "Main site" pill on
  // management surfaces (admin, staff, head-office, kiosk, lobby,
  // signage). The AdminNav already has an explicit "Main Page" link
  // in the header, so the floating pill just crowds the workspace.
  const _p = location.pathname;
  if (
    _p.startsWith("/admin") ||
    _p.startsWith("/staff") ||
    _p.startsWith("/np/join") ||
    _p.startsWith("/book/menu") ||
    _p.startsWith("/t/") ||
    _p.startsWith("/head-office") ||
    _p.startsWith("/checkin") ||
    _p.startsWith("/welcome") ||
    _p.startsWith("/signage") ||
    _p.startsWith("/tv") ||
    _p.startsWith("/lobby") ||
    _p.startsWith("/kiosk")
  ) {
    return null;
  }

  const label = language === "fr" ? "Accueil" : "Main site";

  // Public inner page — bigger champagne pill that visitors notice.
  // Iter 194 · Jul 30 2026 — Karine reported multi-tap issues, so we
  // enforce a 48pt-min hit area + tap-highlight so the button reacts
  // on the first press even on iOS Safari.
  return (
    <button
      type="button"
      onClick={() => navigate("/")}
      data-testid="back-to-main-btn"
      className="fixed z-50 bottom-[76px] md:bottom-6 left-4 md:left-6 flex items-center gap-2 shadow-xl transition-all duration-200 rounded-full px-5 py-3 md:px-6 md:py-3.5 min-h-[48px] text-sm md:text-base font-semibold backdrop-blur-sm hover:shadow-2xl active:scale-95 touch-manipulation"
      style={{
        background: "linear-gradient(135deg, #1a1208, #2c1810)",
        color: "#d4a574",
        border: "1.5px solid #a98455",
        WebkitTapHighlightColor: "rgba(212,165,116,0.45)",
      }}
    >
      <Home className="w-4 h-4 md:w-5 md:h-5" />
      <span style={{ fontFamily: "'Playfair Display', serif" }}>
        {language === "fr" ? "Accueil" : "Home"}
      </span>
    </button>
  );
}
